import * as React from "react";
import { Link } from "react-router-dom";
import { useMe } from "../hooks/useMe.js";

/**
 * Persistent "Day streak" badge for the top bar (TEX-2237).
 *
 * The chunky, always-lit Field Day trophy (flame + count + "Day streak"),
 * rendered in BOTH FieldNav (most pages) and MobileAppBar so it's visible
 * site-wide on desktop and mobile. Self-gating: renders nothing until the
 * player has a streak.
 *
 * Interaction: opens a details popover on hover (desktop) or tap (mobile) — the
 * player's streak stats, with the "How streaks work" rules tucked behind an (i)
 * toggle in the header so the popover stays compact by default. The popover
 * carries the "View dashboard" link, so the badge itself is a button (no
 * navigation) and the same gesture model works on both platforms.
 */
// Circumference of the cold-state progress ring (r=15.5 in its 36×36 viewBox),
// used to drive stroke-dashoffset from today's-goal progress.
const RING_CIRCUMFERENCE = 2 * Math.PI * 15.5;

const GOAL_RULES = (goalGames: number, goalQuestions: number): string[] => [
  `Finish ${goalGames} games — or answer ${goalQuestions} questions — to count a day.`,
  "Play every day: your streak grows by 1 for each day you hit the goal.",
  "Your streak is safe through today — it only breaks after a full day off.",
  "Earn a 🛡️ streak saver every 7 days (up to 2). It auto-covers one missed day.",
  "Out of savers? You can restore a single missed day once a week.",
];

export function StreakBadge(): React.ReactElement | null {
  const { data: me } = useMe();
  const [open, setOpen] = React.useState(false);
  // The "how streaks work" rules are tucked behind the (i) toggle so the popover
  // stays compact by default; collapse again whenever the popover itself closes.
  const [showRules, setShowRules] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  // On touch, a tap fires mouseenter→click; this flag suppresses the hover-open
  // so the tap's click cleanly toggles instead of open-then-close.
  const touchedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) setShowRules(false);
  }, [open]);

  // Close on outside pointer / Escape while open.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Only for signed-in players. Anonymous visitors still get a `stats` block
  // (daily_streak 0), so gate on `user` — otherwise the cold "Start your
  // streak" badge would show in the signed-out top bar.
  if (!me?.user) return null;
  const s = me.stats;
  const streak = s.daily_streak ?? 0;
  // No active streak: still show the badge, but "cold" — flame burnt out, muted
  // pill — so it reads as a goal to relight rather than disappearing entirely.
  const cold = streak <= 0;

  const metToday = s.daily_streak_played_today;
  const progress = metToday
    ? 1
    : Math.min(
        1,
        Math.max(
          s.daily_goal_games > 0 ? s.today_games / s.daily_goal_games : 0,
          s.daily_goal_questions > 0 ? s.today_questions / s.daily_goal_questions : 0,
        ),
      );

  return (
    <div
      className="v-field__streak-wrap"
      ref={wrapRef}
      onMouseEnter={() => {
        if (!touchedRef.current) setOpen(true);
      }}
      onMouseLeave={() => {
        if (!touchedRef.current) setOpen(false);
      }}
    >
      <button
        type="button"
        className={`v-field__streak-badge${cold ? " is-cold" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          cold
            ? "Day streak: none yet — start one today. Show details."
            : `Day streak: ${streak} day${streak === 1 ? "" : "s"} in a row. Show details.`
        }
        onTouchStart={() => {
          touchedRef.current = true;
        }}
        onClick={() => setOpen((o) => !o)}
      >
        {cold ? (
          <>
            <span className="v-field__streak-ring" aria-hidden="true">
              <svg viewBox="0 0 36 36">
                <circle className="ring-track" cx="18" cy="18" r="15.5" />
                <circle
                  className="ring-fill"
                  cx="18"
                  cy="18"
                  r="15.5"
                  style={{
                    strokeDasharray: RING_CIRCUMFERENCE,
                    strokeDashoffset: RING_CIRCUMFERENCE * (1 - progress),
                  }}
                />
              </svg>
              <span
                className="flame"
                style={{ filter: `grayscale(${1 - progress})`, opacity: 0.4 + 0.5 * progress }}
              >
                🔥
              </span>
            </span>
            <span className="v-field__streak-badge-cta">Start your streak</span>
          </>
        ) : (
          <>
            <span className="flame" aria-hidden="true">
              🔥
            </span>
            <span className="v-field__streak-badge-text">
              <span className="num">{streak}</span>
              <span className="lbl">Day streak</span>
            </span>
          </>
        )}
      </button>

      {open && (
        <div className="v-field__streak-pop" role="dialog" aria-label="Day streak details">
          <div className="v-field__streak-pop-card">
            <div className="v-field__streak-pop-head">
              <span className="title">
                {cold ? "Start your streak" : `🔥 ${streak}-day streak`}
              </span>
              <div className="v-field__streak-pop-head-actions">
                <button
                  type="button"
                  className={`v-field__streak-pop-info${showRules ? " is-active" : ""}`}
                  aria-label="How streaks work"
                  aria-expanded={showRules}
                  // Only point at the rules region while it's actually in the DOM —
                  // a dangling aria-controls resolves to nothing for AT. aria-expanded
                  // conveys the toggle relationship on its own when collapsed.
                  aria-controls={showRules ? "v-field-streak-rules" : undefined}
                  onClick={() => setShowRules((v) => !v)}
                >
                  <span className="info-i" aria-hidden="true">
                    i
                  </span>
                </button>
                <button
                  type="button"
                  className="v-field__streak-pop-close"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>

            {(s.best_daily_streak > 0 || s.streak_freeze_count > 0) && (
              <div className="v-field__streak-pop-stats">
                {s.best_daily_streak > 0 && (
                  <span>
                    Best: <strong>{s.best_daily_streak}</strong> day
                    {s.best_daily_streak === 1 ? "" : "s"}
                  </span>
                )}
                {s.streak_freeze_count > 0 && (
                  <span>
                    🛡️ <strong>{s.streak_freeze_count}</strong> streak saver
                    {s.streak_freeze_count === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            )}

            {cold && !metToday && (
              <p className="v-field__streak-pop-nudge">
                Hit today&apos;s goal to light your streak 🔥
              </p>
            )}

            <div className="v-field__streak-pop-goal">
              <div className="goal-head">
                <span>Today's goal</span>
                <strong>
                  {metToday ? "Done ✓" : `${s.today_games}/${s.daily_goal_games} games`}
                </strong>
              </div>
              <div className="goal-bar">
                <span style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
              {!metToday && (
                <span className="goal-alt">
                  or answer {s.today_questions}/{s.daily_goal_questions} questions
                </span>
              )}
            </div>

            {s.daily_streak_at_risk && (
              <p className="v-field__streak-pop-risk">⚠️ Play today to keep your streak alive!</p>
            )}

            {showRules && (
              <div className="v-field__streak-pop-rules" id="v-field-streak-rules">
                <span className="rules-head">How streaks work</span>
                <ul>
                  {GOAL_RULES(s.daily_goal_games, s.daily_goal_questions).map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
            )}

            <Link
              className="v-field__streak-pop-link"
              to="/dashboard"
              onClick={() => setOpen(false)}
            >
              View dashboard →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
